import { useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { isTokenNearExpiry } from '../utils/jwt';

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes — checks exp, refreshes only when near expiry
const SKEW_SECONDS = 5 * 60; // refresh when access token has <5min left
const MAX_RETRIES = 3;
const BACKOFF_MS = [5000, 15000, 30000]; // 5s, 15s, 30s

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

 /**
  * Silently refreshes the JWT access token on an interval.
  *
  * - Refreshes proactively when the access token is near expiry (checked
  *   every REFRESH_INTERVAL_MS, not on a fixed clock).
  * - On refresh failure: retries MAX_RETRIES times with backoff.
  * - Network errors do NOT log the user out — we keep the session
  *   and retry on the next tick.
  * - Hard logout (redirect to /login) only when the refresh token is
  *   definitively rejected by the backend (non-network GraphQL error).
  * Mount once inside AuthProvider when the user is authenticated.
  */
export function useTokenRefresh() {
  const { isAuthenticated, refreshAccessToken } = useContext(AuthContext);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    let cancelled = false;

    const attemptRefresh = async () => {
      if (cancelled || !isAuthenticated) return;

      // Only refresh when access token is near expiry. With a 7d
      // access token this rarely fires, but it is the safety net
      // for long-lived sessions.
      const storedToken = localStorage.getItem('token');
      if (!storedToken || !isTokenNearExpiry(storedToken, SKEW_SECONDS)) {
        return;
      }

      let lastError = null;
      let success = false;

      for (let i = 0; i < MAX_RETRIES; i++) {
        if (cancelled) return;
        try {
          const result = await refreshAccessToken();
          if (result) {
            success = true;
            break;
          } else {
            // refreshAccessToken returned null → hard reject.
            // Stop retrying, it will have triggered logout already.
            return;
          }
        } catch (err) {
          lastError = err;
          // Wait backoff before next retry. On last attempt, skip wait.
          if (i < MAX_RETRIES - 1) {
            await sleep(BACKOFF_MS[i]);
          }
        }
      }

      if (!success && !cancelled && lastError) {
        // All retries exhausted on network errors → preserve session,
        // let next interval/tick retry again. Do NOT logout.
        // eslint-disable-next-line no-console
        console.warn('Token refresh temporarily unavailable — will retry next tick.');
      }
    };

    const tick = () => {
      attemptRefresh().finally(() => {
        if (!cancelled && isAuthenticated) {
          timerRef.current = setTimeout(tick, REFRESH_INTERVAL_MS);
        }
      });
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isAuthenticated, refreshAccessToken]);
}